import { data, Link, redirect, useLoaderData, useNavigate } from 'react-router'
import { resolveDistinctId } from '#app/analytics/attribution.server.ts'
import { type RouteAnalyticsHandle } from '#app/analytics/constants.ts'
import { listingLinkHandler } from '#app/analytics/listing.ts'
import { ClaimEvidence } from '#app/components/claim-evidence.tsx'
import { Dateline } from '#app/components/dateline.tsx'
import { FilterCombobox } from '#app/components/filter-combobox.tsx'
import { QuoteRequestForm } from '#app/components/quote-request-form.tsx'
import { RankingTable } from '#app/components/ranking-table.tsx'
import { ScoreMark } from '#app/components/score.tsx'
import { ThemeFilter } from '#app/components/theme-filter.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Separator } from '#app/components/ui/separator.tsx'
import { loadClaimsForBusinesses } from '#app/scoring/claims.server.ts'
import {
	RANK_ORDER,
	RANKING_ELIGIBLE,
	categoryHref,
	getPublishableCitiesForCategory,
	getRankedCategorySlugs,
} from '#app/scoring/publishable-pages.server.ts'
import {
	CORPUS_MIN_REVIEWS,
	DEPTH_FLOORS,
	RANK_LIMIT,
	clearsRankingFloor,
} from '#app/scoring/scoring-model.ts'
import { authorReference, buildPersonLd } from '#app/seo/author.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { recordListingImpressions } from '#app/utils/business-metrics.server.ts'
import { categoryPlural } from '#app/utils/category-name.ts'
import { prisma } from '#app/utils/db.server.ts'
import { PAGE_MEASURE } from '#app/utils/layout.ts'
import { formatPhoneNumber, generateSlug, pluralize } from '#app/utils/misc.tsx'
import { getNewestReviewTimestamp } from '#app/utils/newest-review.server.ts'
import { quoteRequestAction } from '#app/utils/quote-request.server.ts'
import { type Route } from './+types/$citySlug_.$categorySlug.ts'

/**
 * Local ranking: /{city}/{category}
 *
 * Deliberately the URL that already existed rather than a new
 * /best/{category}/{city}. Two URLs for "roofers in Dallas" would compete for
 * one cluster, and these paths are already indexed, so improving them in place
 * consolidates crawl history instead of starting from zero and redirecting
 * later.
 *
 * The split with /best/{category} is geographic, and that is what keeps them
 * from cannibalizing each other:
 *
 *   /best/{category}    "best roofers in DFW"
 *   /{city}/{category}  "best roofers in Fort Worth"
 *
 * Both use "Best", because both are ranked lists and that is the query people
 * type. An earlier version reserved the word for the metro page, which made
 * this page worse at its own query to avoid a collision that was never there:
 * cannibalization needs two pages chasing the SAME query, and these differ by
 * geo. The lists differ too. Measured on roofing, only 3 of Fort Worth's top
 * 15 also appear in the metro top 15.
 *
 * Below the depth floor this redirects to the category hub rather than
 * rendering thin. These URLs have crawl history, so a 301 lands their residual
 * equity somewhere useful where a soft-200 or a 404 wastes it.
 */

/** A facet needs this many businesses before it is offered. */
const MIN_FACET_BUSINESSES = 3

export async function loader({ params, request, context }: Route.LoaderArgs) {
	const { citySlug, categorySlug } = params
	/*
	 * Multiple themes, combined with AND — the same contract as /best/. A
	 * single-select filter over dozens of themes makes the reader page through
	 * options one at a time; narrowing is the useful operation.
	 */
	const themeFilters = new URL(request.url).searchParams.getAll('mentions')

	const [city, category, subcategory] = await Promise.all([
		prisma.city.findUnique({
			where: { slug: citySlug },
			select: { id: true, name: true, slug: true },
		}),
		prisma.category.findUnique({
			where: { slug: categorySlug },
			select: { id: true, name: true, slug: true, pluralName: true },
		}),
		prisma.subcategory.findUnique({
			where: { slug: categorySlug },
			select: {
				id: true,
				name: true,
				slug: true,
				pluralName: true,
				category: {
					select: { id: true, name: true, slug: true, pluralName: true },
				},
			},
		}),
	])

	if (!city) {
		throw new Response('City not found', { status: 404 })
	}

	// The slug may name a subcategory rather than a category.
	const resolvedCategory = category ?? subcategory?.category ?? null

	if (!resolvedCategory) {
		throw new Response('Category not found', { status: 404 })
	}

	const entity = subcategory ?? resolvedCategory

	/*
	 * Eligibility matches /best/ exactly: scored, published, open, and not
	 * excluded by the ranking review. Counting every published listing while
	 * ordering by score would rebuild the thin "rankings" the floor exists to
	 * prevent.
	 */
	const allScored = await prisma.business.findMany({
		where: {
			...RANKING_ELIGIBLE,
			cities: { some: { id: city.id } },
			...(subcategory
				? { subcategories: { some: { id: subcategory.id } } }
				: { categories: { some: { id: resolvedCategory.id } } }),
		},
		orderBy: [...RANK_ORDER],
		select: {
			id: true,
			slug: true,
			name: true,
			score: true,
			scoreComputedAt: true,
			ratingValue: true,
			ratingCount: true,
			reviewsHeld: true,
			bestFor: true,
			whyItRanks: true,
			pros: true,
			watchOuts: true,
			themes: true,
			phone: true,
			synthesisClaims: true,
			synthesisAt: true,
		},
	})

	/*
	 * Below the floor, redirect rather than render. These URLs are indexed, so
	 * a 301 keeps their equity pointed at something real.
	 *
	 * The destination is resolved rather than assumed to be the hub. Once a
	 * category's ranking publishes, its hub 301s to /best/{slug}, so a fixed
	 * redirect here produced a two-hop chain — city → hub → metro ranking — for
	 * every city URL in a publishable category, which against current data is
	 * all of them.
	 *
	 * The floor covers both depth and corpus coverage: a city with eight scored
	 * businesses and no fetched reviews can order them only by Bayesian rating,
	 * volume and completeness, with the other 58% of the model's weight sitting
	 * at neutral for all eight. Shares clearsRankingFloor with the sitemap.
	 */
	if (
		!clearsRankingFloor({
			total: allScored.length,
			// allScored is already ordered by score desc, so the ranked slice is
			// its head. Coverage is measured there and not across the tail we
			// deliberately never bought reviews for.
			measuredInRankedSlice: allScored
				.slice(0, RANK_LIMIT)
				.filter((business) => (business.reviewsHeld ?? 0) >= CORPUS_MIN_REVIEWS)
				.length,
			depthFloor: DEPTH_FLOORS.cityCategory,
		})
	) {
		throw redirect(
			categoryHref(entity.slug, await getRankedCategorySlugs()),
			301,
		)
	}

	const themesBySlug = new Map<string, Set<string>>()
	for (const business of allScored) {
		for (const theme of new Set(business.themes)) {
			const slug = generateSlug(theme)
			if (!slug) continue
			const existing = themesBySlug.get(slug)
			if (existing) existing.add(theme)
			else themesBySlug.set(slug, new Set([theme]))
		}
	}

	const matchesFacet = (b: { themes: string[] }, themes: Set<string>) =>
		b.themes.some((theme) => themes.has(theme))

	const facetGroups = [...themesBySlug.entries()]
		.map(([slug, themes]) => ({
			slug,
			label: [...themes].sort(
				(a, b) => a.length - b.length || a.localeCompare(b),
			)[0]!,
			themes,
			count: allScored.filter((b) => matchesFacet(b, themes)).length,
		}))
		.filter((facet) => facet.count >= MIN_FACET_BUSINESSES)
		.sort((a, b) => b.count - a.count)

	const activeFacetGroups = facetGroups.filter((f) =>
		themeFilters.includes(f.slug),
	)

	const matching = activeFacetGroups.length
		? allScored.filter((b) =>
				activeFacetGroups.every((facet) => matchesFacet(b, facet.themes)),
			)
		: allScored
	const businesses = matching.slice(0, RANK_LIMIT)

	const [
		claimsByBusiness,
		newestReview,
		publishableCities,
		categories,
		metroScored,
		metroRankedSlice,
	] = await Promise.all([
		// Claims for the rows on screen. Independent of everything below it, so
		// it belongs in the same round trip rather than ahead of it.
		loadClaimsForBusinesses(businesses),
		/*
		 * Newest review behind this city's ranking. Paired with the last rescore
		 * in the dateline so neither date can be refreshed by re-running a job
		 * over data that has not changed.
		 */
		getNewestReviewTimestamp(allScored.map((b) => b.id)),
		/*
		 * Only cities that clear the floor for THIS category. Offering every
		 * city with any published business sends people to a 301 and teaches
		 * them the wrong set of local pages. Names come back with the slugs, so
		 * no second query is needed.
		 */
		getPublishableCitiesForCategory(entity.slug),
		prisma.category.findMany({
			where: { businesses: { some: { status: 'published' } } },
			select: { name: true, slug: true },
			orderBy: { name: 'asc' },
		}),
		/*
		 * Metro-wide counts, so the page can point somewhere broader honestly.
		 * Both total and measured, because /best/ applies the same coverage floor
		 * this page does — linking on depth alone would advertise a 404.
		 */
		prisma.business.count({
			where: {
				...RANKING_ELIGIBLE,
				categories: { some: { id: resolvedCategory.id } },
			},
		}),
		/*
		 * The metro ranking's own slice, not a category-wide count of measured
		 * businesses. /best/ measures coverage across the top RANK_LIMIT by
		 * score, so anything else here would predict its floor wrongly in both
		 * directions.
		 */
		prisma.business.findMany({
			where: {
				...RANKING_ELIGIBLE,
				categories: { some: { id: resolvedCategory.id } },
			},
			orderBy: [...RANK_ORDER],
			take: RANK_LIMIT,
			select: { reviewsHeld: true },
		}),
	])

	// Keep the current city listed even if it is somehow below floor, so the
	// switcher never renders with no selected value.
	const cities = publishableCities.some((c) => c.slug === city.slug)
		? publishableCities
		: [...publishableCities, { slug: city.slug, name: city.name, count: 0 }]

	// See the note in /best/{category}: impressions are counted server-side,
	// bot-filtered, and never awaited.
	void recordListingImpressions({
		request,
		// Resolved rather than left to the cookie: on a cookieless first hit the
		// engagement guard would otherwise key this visitor on IP+UA, which is a
		// coarser identity than the one search already uses.
		distinctId: resolveDistinctId({ request, context }),
		businessIds: businesses.map((b) => b.id),
	})

	return data({
		city,
		category: resolvedCategory,
		subcategory,
		entity,
		businesses: businesses.map((b) => ({
			...b,
			claims: claimsByBusiness.get(b.id) ?? { pros: [], watchOuts: [] },
		})),
		facets: facetGroups.map(({ slug, label, count }) => ({
			slug,
			label,
			count,
		})),
		activeFacets: activeFacetGroups.map(({ slug, label, count }) => ({
			slug,
			label,
			count,
		})),
		cities,
		categories,
		stats: {
			reviewsThrough: newestReview?.toISOString() ?? null,
			scoredAt:
				allScored
					.reduce<Date | null>(
						(latest, b) =>
							b.scoreComputedAt && (!latest || b.scoreComputedAt > latest)
								? b.scoreComputedAt
								: latest,
						null,
					)
					?.toISOString() ?? null,
			totalScored: allScored.length,
			matchingCount: matching.length,
			analyzed: allScored.filter((b) => b.synthesisAt).length,
			reviewsRead: allScored.reduce((sum, b) => sum + (b.reviewsHeld ?? 0), 0),
			metroScored,
			metroPublishable: clearsRankingFloor({
				total: metroScored,
				measuredInRankedSlice: metroRankedSlice.filter(
					(business) => (business.reviewsHeld ?? 0) >= CORPUS_MIN_REVIEWS,
				).length,
				depthFloor: DEPTH_FLOORS.bestMetro,
			}),
		},
	})
}

/**
 * The quote request posts back to the page it was rendered on, so the form
 * works without JavaScript and a failed submission keeps the reader in place
 * with the ranking still on screen.
 */
export async function action({ request, context }: Route.ActionArgs) {
	return quoteRequestAction({ request, context })
}

export const handle = {
	// Read by useRouteAnalytics in root.tsx; tags every pageview and
	// client event fired on this route with its page archetype.
	analytics: { pageType: 'city_category' } satisfies RouteAnalyticsHandle,
	breadcrumb({
		data,
		params,
	}: {
		data: Awaited<ReturnType<typeof loader>>['data']
		params: Record<string, string>
	}) {
		if (!data?.city || !data?.entity) return null
		return {
			name: `${categoryPlural(data.entity)} in ${data.city.name}`,
			url: `/${params.citySlug}/${params.categorySlug}`,
		}
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		if (!loaderData?.city || !loaderData?.entity) return null
		const { city, entity, businesses, stats, activeFacets } = loaderData
		const plural = categoryPlural(entity)
		const canonical = `${origin}/${city.slug}/${entity.slug}`

		/*
		 * "Best X in Y" is the shape of the query and the shape listicle and
		 * answer-engine retrieval looks for. The geo modifier is what separates
		 * this from the metro ranking.
		 */
		const facetLabels = activeFacets.map((f) => f.label).join(' and ')
		const title = activeFacets.length
			? `Best ${plural} in ${city.name} for ${facetLabels}`
			: `Best ${plural} in ${city.name}, Ranked by Review Analysis`

		const description = activeFacets.length
			? `The ${stats.matchingCount} best ${plural.toLowerCase()} in ${city.name} whose reviews repeatedly mention ${facetLabels}.`
			: `The best ${plural.toLowerCase()} in ${city.name}, ranked from ${stats.totalScored} scored businesses on review sentiment, rating, volume and recency. ${stats.reviewsRead.toLocaleString()} reviews analyzed. No paid placement.`

		return {
			title,
			description,
			canonical,
			robots: activeFacets.length ? 'noindex, follow' : undefined,
			openGraph: { title, description, type: 'article', url: canonical },
			twitter: { card: 'summary_large_image', title, description },
			structuredData: activeFacets.length
				? []
				: [
						buildItemListLd({
							name: title,
							items: businesses.map((b) => ({
								name: b.name,
								url: `${origin}/business/${b.slug}`,
							})),
							author: authorReference({ origin }),
							dateModified: loaderData.stats.scoredAt ?? undefined,
						}),
						buildPersonLd({ origin }),
					],
		}
	},
}

export default function CityCategoryPage() {
	const {
		city,
		category,
		subcategory,
		entity,
		businesses,
		facets,
		activeFacets,
		cities,
		categories,
		stats,
	} = useLoaderData<typeof loader>()
	const navigate = useNavigate()

	const plural = categoryPlural(entity)
	const basePath = `/${city.slug}/${entity.slug}`

	return (
		<div className={`${PAGE_MEASURE} py-12`}>
			<h1 className="mb-3 font-serif text-3xl tracking-tight md:text-4xl">
				Best {plural} in {city.name}
			</h1>

			<Dateline
				stats={{
					reviewsThrough: stats.reviewsThrough,
					scoredAt: stats.scoredAt,
					scored: stats.totalScored,
					analyzed: stats.analyzed,
					reviewsRead: stats.reviewsRead,
				}}
			/>

			<p className="text-muted-foreground mb-6 max-w-3xl text-lg leading-relaxed">
				{activeFacets.length
					? `${stats.matchingCount} ${plural.toLowerCase()} in ${city.name} have reviews that repeatedly mention ${activeFacets.map((f) => f.label).join(' and ')}${stats.matchingCount > businesses.length ? `, and these are the ${businesses.length} highest scoring` : ''}.`
					: `We track ${stats.totalScored} ${plural.toLowerCase()} in ${city.name} and have read ${stats.reviewsRead.toLocaleString()} of their reviews. They are ranked below on what customers actually wrote, not on star ratings alone.`}
			</p>

			<div className="text-muted-foreground mb-8 flex flex-wrap gap-x-6 gap-y-2 text-sm">
				<span>
					{stats.totalScored} scored in {city.name}
				</span>
				<span>{stats.analyzed} with review analysis</span>
				{stats.metroPublishable ? (
					<Link
						className="underline underline-offset-4"
						to={`/best/${category.slug}`}
					>
						See the metro-wide ranking ({stats.metroScored})
					</Link>
				) : null}
				<Link className="underline underline-offset-4" to="/scoring-method">
					How we score
				</Link>
			</div>

			{facets.length > 0 ? (
				<ThemeFilter
					basePath={basePath}
					facets={facets}
					active={activeFacets}
				/>
			) : null}

			<RankingTable businesses={businesses} />

			<div className="space-y-8">
				{businesses.map((business, index) => (
					<div key={business.id}>
						<div className="mb-3 flex items-stretch gap-4">
							{business.score != null ? (
								<ScoreMark score={business.score} rank={index + 1} size="lg" />
							) : null}
							<div className="min-w-0 flex-1">
								<h2 className="font-serif text-xl tracking-tight md:text-2xl">
									<Link
										className="hover:underline"
										to={`/business/${business.slug}`}
										{...listingLinkHandler('listing_result_clicked', {
											businessId: business.id,
											pageType: 'city_category',
											categorySlug: entity.slug,
											citySlug: city.slug,
											placement: 'ranking_list',
											position: index + 1,
										})}
									>
										{business.name}
									</Link>
								</h2>
								<div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
									<span>
										{business.ratingValue?.toFixed(1)} stars from{' '}
										{(business.ratingCount ?? 0).toLocaleString()} reviews
									</span>
									{business.reviewsHeld ? (
										<span>{business.reviewsHeld} read in full</span>
									) : null}
								</div>
								{/*
								 * Text, not a Badge.
								 *
								 * The Badge base carries whitespace-nowrap AND shrink-0, so
								 * a bestFor string could neither wrap nor compress and forced
								 * the whole entry wider than a phone viewport — which is what
								 * put a horizontal scrollbar on this page. These run to 69
								 * characters (median 41); they are sentences, and a badge is
								 * for short labels. This also matches how the business page
								 * has always rendered the same field.
								 */}
								{business.bestFor ? (
									<p className="text-muted-foreground mt-2 text-sm">
										Best for: {business.bestFor}
									</p>
								) : null}
							</div>
						</div>

						{business.whyItRanks ? (
							<p className="text-muted-foreground mb-4 max-w-3xl leading-relaxed">
								{business.whyItRanks}
							</p>
						) : null}

						{business.claims.pros.length > 0 ? (
							<div className="mb-3">
								<h3 className="mb-2 text-sm font-medium">
									What reviewers consistently mention
								</h3>
								<ul className="space-y-2">
									{business.claims.pros.map((claim, i) => (
										<ClaimEvidence key={`pro-${i}`} claim={claim} />
									))}
								</ul>
							</div>
						) : null}

						{business.claims.watchOuts.length > 0 ? (
							<div className="mb-3">
								<h3 className="mb-2 text-sm font-medium">Watch out for</h3>
								<ul className="space-y-2">
									{business.claims.watchOuts.map((claim, i) => (
										<ClaimEvidence
											key={`watch-${i}`}
											claim={claim}
											tone="caution"
										/>
									))}
								</ul>
							</div>
						) : null}

						<div className="mt-4 flex flex-wrap gap-2">
							<Button asChild size="sm" variant="outline">
								<Link to={`/business/${business.slug}`}>View listing</Link>
							</Button>
							{business.phone ? (
								<Button asChild size="sm" variant="outline">
									{/* Dial the raw number, show the formatted one. */}
									<a href={`tel:${business.phone}`}>
										{formatPhoneNumber(business.phone)}
									</a>
								</Button>
							) : null}
						</div>

						{index < businesses.length - 1 ? (
							<Separator className="mt-8" />
						) : null}
					</div>
				))}
			</div>

			{/*
			 * Below the ranking, never above — see /best/.
			 *
			 * categoryId is the parent category, not `entity`.
			 * QuoteRequest.categoryId is a FK to Category, and on a subcategory URL
			 * `entity` is a Subcategory — so demand would either fail the
			 * constraint or be filed against the wrong table.
			 */}
			<QuoteRequestForm
				categoryId={category.id}
				categoryName={entity.name}
				cityId={city.id}
				cityName={city.name}
				sourcePath={`/${city.slug}/${entity.slug}`}
			/>

			<Separator className="my-10" />

			<div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
				{cities.length > 0 ? (
					<FilterCombobox
						emptyText="No cities found"
						onValueChange={(value) => void navigate(`/${value}/${entity.slug}`)}
						options={cities.map((c) => ({ label: c.name, value: c.slug }))}
						placeholder="Change city"
						searchPlaceholder="Search cities..."
						value={city.slug}
					/>
				) : null}
				{categories.length > 0 ? (
					<FilterCombobox
						emptyText="No categories found"
						onValueChange={(value) => void navigate(`/${city.slug}/${value}`)}
						options={categories.map((c) => ({ label: c.name, value: c.slug }))}
						placeholder="Change category"
						searchPlaceholder="Search categories..."
						value={subcategory ? category.slug : entity.slug}
					/>
				) : null}
			</div>

			<div className="text-muted-foreground text-sm leading-relaxed">
				<p className="mb-2">
					Ranked on the{' '}
					<Link className="underline underline-offset-4" to="/scoring-method">
						DFW Professionals scoring method
					</Link>
					. Every strength and caution above is supported by at least two
					separate reviews, and you can expand any of them to read the source.
				</p>
				<p>
					No business paid for a position on this page, and none can. Spotted an
					error?{' '}
					<Link className="underline underline-offset-4" to="/contact">
						Tell us
					</Link>
					.
				</p>
			</div>
		</div>
	)
}
